import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";

const GoalDeltaIndicator = ({ userId, goalOverride, currentOverride }) => {
  const [deltaInfo, setDeltaInfo] = useState(null);

  const fetchDeltaInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agent/goal-delta/${userId}`);
      setDeltaInfo(response.data);
    } catch (error) {
      console.error("Error fetching goal delta:", error);
    }
  }, [userId]);

  useEffect(() => {
    fetchDeltaInfo();
    const interval = setInterval(fetchDeltaInfo, 30000);
    return () => clearInterval(interval);
  }, [fetchDeltaInfo]);

  if (!deltaInfo) return null;

  // Use instance-aware overrides if provided
  const effectiveGoal = goalOverride !== undefined && goalOverride !== null ? goalOverride : null;
  const effectiveCurrent = currentOverride !== undefined && currentOverride !== null ? currentOverride : null;
  const effectiveDelta = (effectiveGoal !== null && effectiveCurrent !== null) 
    ? (effectiveGoal - effectiveCurrent) 
    : deltaInfo.delta;
  const effectiveProgress = (effectiveGoal !== null && effectiveCurrent !== null && effectiveGoal > 0)
    ? (effectiveCurrent / effectiveGoal * 100)
    : deltaInfo.progressPercentage;
  
  // Recalculate intensity based on effective progress
  let effectiveIntensity = deltaInfo.intensity;
  let effectiveFrequency = deltaInfo.actionFrequency;
  if (effectiveGoal !== null) {
    if (effectiveProgress >= 90) { effectiveIntensity = "maintain"; effectiveFrequency = "low"; }
    else if (effectiveProgress >= 70) { effectiveIntensity = "steady"; effectiveFrequency = "medium"; }
    else if (effectiveProgress >= 40) { effectiveIntensity = "aggressive"; effectiveFrequency = "high"; }
    else { effectiveIntensity = "urgent"; effectiveFrequency = "very_high"; }
  }

  // Smart formatting — never show 0.X
  const formatGap = (amount) => {
    if (amount >= 1000000) {
      const m = amount / 1000000;
      return m % 1 === 0 ? `$${m.toFixed(0)}M` : `$${m.toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case "urgent": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "aggressive": return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      case "steady": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "maintain": return "bg-green-500/20 text-green-400 border-green-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const getIntensityIcon = (intensity) => {
    if (intensity === "urgent" || intensity === "aggressive") {
      return <TrendingUp className="h-4 w-4" />;
    } else if (intensity === "maintain") {
      return <Activity className="h-4 w-4" />;
    }
    return <TrendingDown className="h-4 w-4" />;
  };

  return (
    <div className="fixed bottom-6 right-6 z-30" data-testid="goal-delta-indicator">
      <div className="bg-[#0f1419] border border-gray-800/50 rounded-lg p-4 shadow-xl backdrop-blur-lg">
        <div className="flex items-center gap-3">
          <Badge className={`${getIntensityColor(effectiveIntensity)} border px-3 py-1 flex items-center gap-2`}>
            {getIntensityIcon(effectiveIntensity)}
            <span className="capitalize font-semibold">{effectiveIntensity}</span>
          </Badge>
          <div className="text-right">
            <p className="text-xs text-gray-400">Gap to Goal</p>
            <p className="text-sm font-bold text-white">
              {formatGap(effectiveDelta)}
            </p>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-800">
          <p className="text-xs text-gray-500">
            Agent Frequency: <span className="text-[#3dd9c5] font-semibold capitalize">{effectiveFrequency.replace('_', ' ')}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoalDeltaIndicator;
